import React from 'react';
import { Card, Box, Typography, Stack, alpha } from '@mui/material';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from 'recharts';

const COLORS = ['#FE5F01', '#3B82F6', '#10B981', '#F59E0B', '#8B5CF6', '#EC4899', '#06B6D4', '#64748B', '#F97316'];

const fmt = (n) => Number(n || 0).toLocaleString('en-IN');

const DonutTooltip = ({ active, payload }) => {
  if (!active || !payload?.length) return null;
  const p = payload[0];
  return (
    <Box
      sx={{
        bgcolor: 'background.paper',
        borderRadius: 2,
        boxShadow: '0 12px 24px rgba(20,26,56,0.20)',
        border: '1px solid',
        borderColor: 'divider',
        p: 1.25,
        minWidth: 120,
      }}
    >
      <Stack direction="row" alignItems="center" spacing={0.75}>
        <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: p.payload.fill }} />
        <Typography sx={{ fontSize: 12, fontWeight: 700 }}>{p.name}</Typography>
      </Stack>
      <Typography sx={{ fontSize: 14, fontWeight: 800, mt: 0.25 }}>{fmt(p.value)}</Typography>
    </Box>
  );
};

const DonutCard = ({ title, subtitle, data = [], height = 280, valueKey = 'count', labelKey = 'name' }) => {
  const total = data.reduce((s, d) => s + (d[valueKey] || 0), 0);
  return (
    <Card sx={{ p: { xs: 2, md: 3 }, borderRadius: 4, height: '100%', width: '100%', maxWidth: '100%' }}>
      <Box mb={2}>
        <Typography variant="h6" sx={{ fontWeight: 800 }}>{title}</Typography>
        {subtitle && <Typography variant="body2" color="text.secondary">{subtitle}</Typography>}
      </Box>
      {data.length === 0 ? (
        <Box sx={{ height, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Typography color="text.secondary">No data</Typography>
        </Box>
      ) : (
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="center">
          <Box sx={{ width: { xs: '100%', sm: '50%' }, height, position: 'relative' }}>
            <ResponsiveContainer>
              <PieChart>
                <Pie
                  data={data}
                  dataKey={valueKey}
                  nameKey={labelKey}
                  innerRadius="62%"
                  outerRadius="92%"
                  paddingAngle={2}
                  strokeWidth={0}
                >
                  {data.map((_, i) => (
                    <Cell key={i} fill={COLORS[i % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip content={<DonutTooltip />} />
              </PieChart>
            </ResponsiveContainer>
            <Box
              sx={{
                position: 'absolute',
                inset: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                pointerEvents: 'none',
              }}
            >
              <Box textAlign="center">
                <Typography sx={{ fontSize: 11, fontWeight: 700, color: 'text.disabled', letterSpacing: 0.6 }}>
                  TOTAL
                </Typography>
                <Typography sx={{ fontSize: 22, fontWeight: 800, lineHeight: 1.1 }}>
                  {fmt(total)}
                </Typography>
              </Box>
            </Box>
          </Box>
          <Stack spacing={0.75} sx={{ width: { xs: '100%', sm: '50%' } }}>
            {data.slice(0, 8).map((d, i) => {
              const pct = total ? Math.round((d[valueKey] / total) * 100) : 0;
              const color = COLORS[i % COLORS.length];
              return (
                <Stack
                  key={d[labelKey] + i}
                  direction="row"
                  alignItems="center"
                  spacing={1}
                  sx={{
                    px: 1,
                    py: 0.75,
                    borderRadius: 1.5,
                    '&:hover': { bgcolor: alpha(color, 0.06) },
                  }}
                >
                  <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: color, flexShrink: 0 }} />
                  <Typography variant="body2" sx={{ flex: 1, fontWeight: 600 }} noWrap>
                    {d[labelKey]}
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 800, color: 'text.primary' }}>
                    {fmt(d[valueKey])}
                  </Typography>
                  <Typography variant="caption" sx={{ color: 'text.disabled', fontWeight: 700, width: 36, textAlign: 'right' }}>
                    {pct}%
                  </Typography>
                </Stack>
              );
            })}
          </Stack>
        </Stack>
      )}
    </Card>
  );
};

export default React.memo(DonutCard);
